import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import { UserIcon } from "../../../icons";
import { AnnotisedMessage } from "../../Chat/ChatBody";
import formatDateTime from "../../Common/formatDateTime";
import FullPageProgress from "../../FullPageProgress";
import { ToastManager } from "../../Toast/ToastManager";
import EventReplayChatBottom from "./EventReplayChatBottom";
import EventReplayChatTop from "./EventReplayChatTop";
import { SESSION_AUTH } from "../../../common/auth";
import { MESSAGE_SERVICES, REALTIME_SERVICES } from "../../../services";

const EventReplayChat = props => {
  const { chatHeight, userData, eventId, onRegister } = props;
  const { LOGOUT_USER_SESSION } = SESSION_AUTH();
  const [sending, setSending] = useState(false);
  const [queryMsg, setQueryMsg] = useState("");
  const [messageList, setMessageList] = useState([]);
  const dataFetched = useRef(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const chatBodyRef = useRef(null);
  const [scrollPrevBottom, setScrollPrevBottom] = useState(0);
  const [visibleChat, setVisibleChat] = useState(true);

  useEffect(() => {
    if (!eventId || !userData) return;

    const realtime = REALTIME_SERVICES.connect("/event", realtime => {
      realtime.emit("joinEvent", { eventId: eventId });
    });

    realtime.on("newEventMsg", data => {
      if (data?.code === 200) {
        updateData([data.data]);
      }
    });

    return () => {
      REALTIME_SERVICES.disconnect("/event");
    };
  }, [eventId, userData]);

  const updateData = (data, nmsg = false) => {
    if (data && data?.length > 0) {
      setMessageList(old => {
        const merged = nmsg ? [...old, ...data] : [...data, ...old];
        const sorted = merged.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
        return sorted;
      });
    }
  };

  useEffect(() => {
    if (!eventId || dataFetched.current) return;
    dataFetched.current = true;
    if (page > 1 && !userData) {
      return onRegister();
    }
    setLoading(true);
    if (page === 1) {
      setMessageList([]);
    }
    MESSAGE_SERVICES.event({
      userId: userData?.userId,
      org: userData?.org?.id,
      id: eventId,
      page: page,
    })
      .then(data => {
        if (data.code === 200) {
          updateData(data.data?.list);
          setHasMore(data?.data?.hasMore);
          setTimeout(function () {
            scrollToBottom("auto", scrollPrevBottom);
          });
        } else if (data.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast({ title: data.message });
        } else {
          ToastManager.addToast({ title: data.message });
        }
      })
      .catch(err => {
        ToastManager.addToast({ title: err.message });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LOGOUT_USER_SESSION, eventId, userData, dataFetched, page]);

  const sendMessage = async () => {
    try {
      let q = queryMsg.trim();
      if (!q) return;
      if (!userData) {
        return onRegister();
      }
      const realtime = REALTIME_SERVICES.getRealtime("/event");
      if (!realtime || realtime?.disconnected) {
        ToastManager.addToast({
          title: "Network error",
        });
        return;
      }
      setSending(true);
      let parma = {
        userId: userData?.userId,
        org: userData?.org?.id,
        eventId: eventId,
        message: q,
      };

      realtime.emit("sendEventMsg", parma, res => {
        if (res.code === 200) {
          setQueryMsg("");
          scrollToBottom("smooth");
        } else if (res.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast({ title: res.message });
        } else {
          ToastManager.addToast({ title: res.message });
        }
        setSending(false);
      });
    } catch (err) {
      ToastManager.addToast({ title: err.message });
      setSending(false);
    }
  };

  const loadmore = () => {
    setScrollPrevBottom(
      chatBodyRef.current.scrollHeight -
        chatBodyRef.current.scrollTop -
        chatBodyRef.current.clientHeight
    );
    dataFetched.current = false;
    setPage(old => old + 1);
  };
  const scrollToBottom = (behavior = "auto", previousScrollFromBottom) => {
    if (chatBodyRef.current) {
      if (previousScrollFromBottom) {
        chatBodyRef.current?.scrollTo({
          top:
            chatBodyRef.current.scrollHeight -
            chatBodyRef.current.clientHeight -
            previousScrollFromBottom,
          behavior: behavior || "auto",
        });
      } else {
        chatBodyRef.current?.scrollTo({
          top: chatBodyRef.current.scrollHeight,
          behavior: behavior || "auto",
        });
      }
    }
  };

  return (
    <div
      className={`attendee-area-col-replay-layout-chat ${
        !visibleChat ? "attendee-area-col-replay-layout-chat-disable" : ""
      }`}
      style={{
        height: messageList?.length > 0 && chatHeight ? chatHeight : "",
      }}
    >
      <EventReplayChatTop
        visibleChat={visibleChat}
        setVisibleChat={setVisibleChat}
      />
      {visibleChat ? (
        <>
          <div
            className="attendee-area-col-replay-layout-chat-body"
            ref={chatBodyRef}
          >
            {loading ? (
              <FullPageProgress size={50} />
            ) : hasMore ? (
              <div className="text-center">
                <Button
                  size="small"
                  variant="outlined"
                  className="events6ix-btn events6ix-btn-light events6ix-btn-sm"
                  type="button"
                  sx={{ textTransform: "none" }}
                  onClick={loadmore}
                >
                  Load previous
                </Button>
              </div>
            ) : null}
            {messageList?.length > 0 ? (
              messageList.map((item, key) => ChatmessageUser(item, key))
            ) : !loading ? (
              <div className="attendee-area-col-replay-layout-chat-body-empty">
                There are no messages here
              </div>
            ) : null}
          </div>

          <EventReplayChatBottom
            sending={sending}
            queryMsg={queryMsg}
            setQueryMsg={setQueryMsg}
            sendMessage={sendMessage}
          />
        </>
      ) : null}
    </div>
  );
};

const ChatmessageUser = (message, key) => {
  return (
    <div className="attendee-area-col-replay-layout-chat-body-row" key={key}>
      <Link
        to={`/u/${message?.sender?.username}`}
        className="attendee-area-col-replay-layout-chat-body-row-logo"
      >
        {message?.sender?.profileImage ? (
          <img src={message?.sender?.profileImage} alt="user" />
        ) : (
          <UserIcon />
        )}
      </Link>
      <div className="attendee-area-col-replay-layout-chat-body-row-content">
        <div className="attendee-area-col-replay-layout-chat-body-row-content-top">
          <Link
            to={`/u/${message?.sender?.username}`}
            className="attendee-area-col-replay-layout-chat-body-row-content-top-name text-link"
          >
            {message?.sender?.name}
          </Link>
          <div className="attendee-area-col-replay-layout-chat-body-row-content-top-date">
            {formatDateTime(message?.createdAt)}
          </div>
        </div>
        <div className="attendee-area-col-replay-layout-chat-body-row-content-text">
          <AnnotisedMessage message={message?.message} />
        </div>
      </div>
    </div>
  );
};

export default EventReplayChat;
